<template>
  <div v-if="ad" class="ad-wrapper">
    <!-- 🖼 Manual Ad -->
    <div
      v-if="ad.provider === 'manual'"
      class="ad-box"
      @click="openManualAd"
    >
      <img
        :src="ad.image_url"
        :alt="ad.title"
        class="ad-img"
        loading="lazy"
      />
    </div>

    <!-- 🟨 Google Ad -->
    <div v-else-if="ad.provider === 'google'" class="ad-box google-box">
      <ins
        class="adsbygoogle"
        style="display:block"
        data-ad-client="ca-pub-XXXXXXXXXXXX"
        :data-ad-slot="ad.google_slot_id"
        data-ad-format="auto"
        data-full-width-responsive="true"
      ></ins>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */

import { ref, onMounted } from "vue";
import { supabase } from "@/supabase";

// Props: page position
const props = defineProps({
  position: {
    type: String,
    required: true, // e.g., "bottom", "sidebar", etc.
  },
});

const ad = ref(null);

// 🧠 Fetch active ad from Supabase
const loadAd = async () => {
  try {
    const { data, error } = await supabase
      .from("ads")
      .select("*")
      .eq("position", props.position)
      .eq("active", true)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.warn("⚠️ No ad found for position:", props.position, error.message);
      return;
    }

    if (data) {
      ad.value = data;

      // If it's a Google ad → load script
      if (data.provider === "google") {
        loadGoogleScript();
      }
    }
  } catch (err) {
    console.error("❌ Error loading ad:", err);
  }
};

// ✅ Load Google AdSense script dynamically
function loadGoogleScript() {
  if (
    document.querySelector(
      'script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]'
    )
  ) {
    // Script already loaded
    setTimeout(() => {
      if (window.adsbygoogle) window.adsbygoogle.push({});
    }, 500);
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src =
    "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
  script.setAttribute("data-ad-client", "ca-pub-XXXXXXXXXXXX"); // 🔹 Replace this with your AdSense Publisher ID
  document.head.appendChild(script);

  script.onload = () => {
    console.log("✅ Google Ads script loaded");
    if (window.adsbygoogle) window.adsbygoogle.push({});
  };
}

// 🖱️ When user clicks manual ad
function openManualAd() {
  if (ad.value && ad.value.redirect_url) {
    window.open(ad.value.redirect_url, "_blank");
  }
}

onMounted(() => {
  loadAd();
});

</script>

<style scoped>
/* 🧱 OUTER WRAPPER */
.ad-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 📦 FIXED AD BOX (same for all ads) */
.ad-box {
  max-width: 420px;     /* ⬅️ WAS 300px */
  width: 92%;
  max-height: 320px;    /* ⬅️ WAS 220px */
  padding: 12px;
  border-radius: 16px;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  justify-content: center;
  align-items: center;
}

.ad-img {
  max-width: 100%;
  max-height: 300px;    /* ⬅️ WAS 200px */
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 14px;
}

/* 🟨 GOOGLE ADS FIX */
.google-box {
  overflow: hidden;
}
@media (max-width: 768px) {
  .ad-box {
    max-width: 90%;
    max-height: 260px;
  }

  .ad-img {
    max-height: 240px;
  }
}
</style>